import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { 
  Palette, 
  Plus, 
  Pencil, 
  Trash2, 
  Star,
  X,
  Save,
  Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FONT_OPTIONS = [
  { value: "Cormorant Garamond", label: "Cormorant Garamond (Elegant)" },
  { value: "Playfair Display", label: "Playfair Display (Classic)" },
  { value: "Lora", label: "Lora (Traditional)" },
  { value: "Merriweather", label: "Merriweather (Readable)" },
  { value: "Inter", label: "Inter (Modern)" },
  { value: "system-ui", label: "System Default" }
];

const HEADER_STYLES = [
  { value: "classic", label: "Classic" },
  { value: "modern", label: "Modern" },
  { value: "minimal", label: "Minimal" }
];

const PRODUCT_CARD_STYLES = [
  { value: "elevated", label: "Elevated (Shadow)" },
  { value: "flat", label: "Flat" },
  { value: "bordered", label: "Bordered" }
];

// Color Input Component
function ColorInput({ label, value, onChange }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold">{label}</Label>
      <div className="flex gap-2">
        <input
          type="color"
          value={value || "#ffffff"}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded cursor-pointer border shadow-sm"
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="flex-1"
        />
      </div>
    </div>
  );
}

// Theme Card Component
function ThemeCard({ theme, isDefault, onSetDefault, onEdit, onDelete }) {
  return (
    <div 
      className={`bg-white rounded-2xl border ${isDefault ? 'border-primary ring-2 ring-primary/20' : 'border-slate-200'} overflow-hidden shadow-sm hover:shadow-md transition-all`}
    >
      {/* Color Preview */}
      <div className="h-24 relative overflow-hidden">
        <div className="absolute inset-0 flex">
          <div style={{ backgroundColor: theme.primary_color }} className="flex-1" />
          <div style={{ backgroundColor: theme.secondary_color }} className="flex-1" />
          <div style={{ backgroundColor: theme.accent_color }} className="flex-1" />
        </div>
        <div className="absolute top-2 right-2 flex gap-1 items-start">
          {isDefault && (
            <div className="bg-primary text-primary-foreground text-xs font-semibold px-2 py-1 rounded-full shadow-md flex items-center gap-1">
              <Star className="w-3 h-3 filled" />
              Default
            </div>
          )}
          {!theme.is_preset && (
            <Button
              variant="secondary"
              size="icon"
              className="w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white border-0 shadow-sm transition-all"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(theme);
              }}
              title="Edit Theme"
            >
              <Pencil className="w-3.5 h-3.5 text-slate-600" />
            </Button>
          )}
        </div>
        
        <div className="absolute top-2 left-2 flex gap-1">
          {theme.is_preset && (
            <div className="bg-blue-500 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
              Preset
            </div>
          )}
          {!theme.is_active && (
            <div className="bg-slate-500 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
              Archived
            </div>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-slate-800">{theme.name}</h3>
        {theme.description && (
          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{theme.description}</p>
        )}
        
        {/* Font Preview */}
        <p className="mt-2 text-sm" style={{  color: theme.text_color }}>
          {theme.font_family}
        </p>
        
        {/* Actions */}
        <div className="flex gap-2 mt-4">
          {!isDefault && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSetDefault(theme.theme_id)}
              className="flex-1"
            >
              <Star className="w-4 h-4 mr-1" />
              Set Default
            </Button>
          )}
          {!theme.is_preset && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(theme)}
              className="text-red-500 hover:text-red-600 border-red-100 hover:bg-red-50"
              title="Delete Theme"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminThemes() {
  const [themes, setThemes] = useState({ presets: [], custom: [], all: [] });
  const [loading, setLoading] = useState(true);
  const [defaultThemeId, setDefaultThemeId] = useState("classic_elegant");
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    primary_color: "#1a365d",
    secondary_color: "#2d3748",
    accent_color: "#d69e2e",
    background_color: "#ffffff",
    text_color: "#1a202c",
    font_family: "Cormorant Garamond",
    header_bg_color: "#1a365d",
    header_style: "classic",
    product_card_style: "elevated",
    is_active: true
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("session_token")}`
  });

  const fetchThemes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/themes`, { headers: getAuthHeaders() });
      setThemes(response.data);
      
      // Get default theme
      const defaultRes = await axios.get(`${API}/admin/themes/default/current`, { headers: getAuthHeaders() });
      setDefaultThemeId(defaultRes.data.theme_id);
    } catch (error) {
      toast.error("Failed to load themes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchThemes();
  }, [fetchThemes]);

  const openCreateModal = () => {
    setEditingTheme(null);
    setFormData({
      name: "",
      description: "",
      primary_color: "#1a365d",
      secondary_color: "#2d3748",
      accent_color: "#d69e2e",
      background_color: "#ffffff",
      text_color: "#1a202c",
      font_family: "Cormorant Garamond",
      header_bg_color: "#1a365d",
      header_style: "classic",
      product_card_style: "elevated",
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (theme) => {
    if (theme.is_preset) {
      toast.error("Cannot edit preset themes. Create a custom theme instead.");
      return;
    }
    setEditingTheme(theme);
    setFormData({
      name: theme.name || "",
      description: theme.description || "",
      primary_color: theme.primary_color || "#1a365d",
      secondary_color: theme.secondary_color || "#2d3748",
      accent_color: theme.accent_color || "#d69e2e",
      background_color: theme.background_color || "#ffffff",
      text_color: theme.text_color || "#1a202c",
      font_family: theme.font_family || "Cormorant Garamond",
      header_bg_color: theme.header_bg_color || theme.primary_color || "#1a365d",
      header_style: theme.header_style || "classic",
      product_card_style: theme.product_card_style || "elevated",
      is_active: theme.is_active !== undefined ? theme.is_active : true
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Theme name is required");
      return;
    }
    
    setSaving(true);
    try {
      if (editingTheme) {
        await axios.put(`${API}/admin/themes/${editingTheme.theme_id}`, formData, { headers: getAuthHeaders() });
        toast.success("Theme updated successfully");
      } else {
        await axios.post(`${API}/admin/themes`, formData, { headers: getAuthHeaders() });
        toast.success("Theme created successfully");
      }
      setShowModal(false);
      fetchThemes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save theme");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (theme) => {
    if (theme.is_preset) {
      toast.error("Cannot delete preset themes");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete "${theme.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/themes/${theme.theme_id}`, { headers: getAuthHeaders() });
      toast.success("Theme deleted successfully");
      fetchThemes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete theme");
    }
  };

  const handleSetDefault = async (themeId) => {
    try {
      await axios.put(`${API}/admin/themes/${themeId}/default`, {}, { headers: getAuthHeaders() });
      toast.success("Default theme updated");
      setDefaultThemeId(themeId);
      fetchThemes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set default theme");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Theme Management</h1>
          <p className="text-slate-500 mt-1">Manage store themes and set defaults for new resellers</p>
        </div>
        <Button onClick={openCreateModal} className="bg-primary hover:bg-primary/90">
          <Plus className="w-4 h-4 mr-2" />
          Create Theme
        </Button>
      </div>

      {/* Preset Themes */}
      <div>
        <h2 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <Palette className="w-5 h-5 text-blue-500" />
          Preset Themes
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {themes.presets.map((theme) => (
            <ThemeCard 
              key={theme.theme_id} 
              theme={theme} 
              isDefault={theme.theme_id === defaultThemeId}
              onSetDefault={handleSetDefault}
              onEdit={openEditModal}
              onDelete={handleDelete}
            />
          ))}
        </div>
      </div>

      {/* Custom Themes */}
      <div>
        <h2 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <Palette className="w-5 h-5 text-amber-500" />
          Custom Themes ({themes.custom.length})
        </h2>
        {themes.custom.length === 0 ? (
          <div className="bg-slate-50 rounded-2xl p-8 text-center">
            <Palette className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No custom themes yet</p>
            <Button onClick={openCreateModal} variant="outline" className="mt-4">
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Theme
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {themes.custom.map((theme) => (
              <ThemeCard 
                key={theme.theme_id} 
                theme={theme} 
                isDefault={theme.theme_id === defaultThemeId}
                onSetDefault={handleSetDefault}
                onEdit={openEditModal}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-amber-500" />
              {editingTheme ? "Edit Theme" : "Create New Theme"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Theme Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Midnight Navy"
                />
              </div>
              <div className="space-y-2">
                <Label>Font Family</Label>
                <Select 
                  value={formData.font_family} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, font_family: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FONT_OPTIONS.map((font) => (
                      <SelectItem key={font.value} value={font.value}>{font.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Status</Label>
                  <p className="text-xs text-slate-500">Enable this theme for resellers</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold ${formData.is_active ? 'text-green-600' : 'text-slate-400'}`}>
                    {formData.is_active ? 'Active' : 'Archived'}
                  </span>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your theme..."
                  rows={2}
                />
              </div>
            </div>

            {/* Colors */}
            <div>
              <h3 className="font-semibold text-slate-700 mb-3">Colors</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <ColorInput 
                  label="Primary" 
                  value={formData.primary_color} 
                  onChange={(v) => setFormData(prev => ({ ...prev, primary_color: v }))} 
                />
                <ColorInput 
                  label="Secondary" 
                  value={formData.secondary_color} 
                  onChange={(v) => setFormData(prev => ({ ...prev, secondary_color: v }))} 
                />
                <ColorInput 
                  label="Accent" 
                  value={formData.accent_color} 
                  onChange={(v) => setFormData(prev => ({ ...prev, accent_color: v }))} 
                />
                <ColorInput 
                  label="Background" 
                  value={formData.background_color} 
                  onChange={(v) => setFormData(prev => ({ ...prev, background_color: v }))} 
                />
                <ColorInput 
                  label="Text" 
                  value={formData.text_color} 
                  onChange={(v) => setFormData(prev => ({ ...prev, text_color: v }))} 
                />
                <ColorInput 
                  label="Header Background" 
                  value={formData.header_bg_color} 
                  onChange={(v) => setFormData(prev => ({ ...prev, header_bg_color: v }))} 
                />
              </div>
            </div>

            {/* Style Options */}
            <div>
              <h3 className="font-semibold text-slate-700 mb-3">Style Options</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Header Style</Label>
                  <Select 
                    value={formData.header_style} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, header_style: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HEADER_STYLES.map((style) => (
                        <SelectItem key={style.value} value={style.value}>{style.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Product Card Style</Label>
                  <Select 
                    value={formData.product_card_style} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, product_card_style: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCT_CARD_STYLES.map((style) => (
                        <SelectItem key={style.value} value={style.value}>{style.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div>
              <h3 className="font-semibold text-slate-700 mb-3">Live Preview</h3>
              <div 
                className="rounded-xl p-6 border flex flex-col gap-5 shadow-inner"
                style={{ 
                  backgroundColor: formData.background_color || '#ffffff',
                  fontFamily: formData.font_family || 'Inter'
                }}
              >
                {/* Contrast helper logic */}
                {(() => {
                  const getContrast = (hex) => {
                    if (!hex || hex === 'transparent') return 'white';
                    const r = parseInt(hex.slice(1, 3), 16);
                    const g = parseInt(hex.slice(3, 5), 16);
                    const b = parseInt(hex.slice(5, 7), 16);
                    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                    return (yiq >= 128) ? 'black' : 'white';
                  };

                  const primaryBg = formData.primary_color || '#1a365d';
                  const headerBg = formData.header_bg_color || primaryBg;
                  const accentBg = formData.accent_color || '#d69e2e';
                  const textCol = formData.text_color || '#1a202c';

                  return (
                    <div className="space-y-5">
                      {/* Header Preview */}
                      <div
                        className="h-12 w-full rounded-lg flex items-center px-4 shadow-sm border"
                        style={{ 
                          backgroundColor: headerBg,
                          color: getContrast(headerBg)
                        }}
                      >
                        <span className="text-sm font-bold tracking-tight">Header Preview</span>
                      </div>
                      
                      {/* Buttons Preview */}
                      <div className="flex gap-3">
                        <div
                          className="px-6 py-2 rounded-full text-sm font-bold shadow-sm"
                          style={{ 
                            backgroundColor: primaryBg,
                            color: getContrast(primaryBg)
                          }}
                        >
                          Primary Button
                        </div>
                        <div
                          className="px-6 py-2 rounded-full text-sm font-bold shadow-sm"
                          style={{ 
                            backgroundColor: accentBg,
                            color: getContrast(accentBg)
                          }}
                        >
                          Accent Action
                        </div>
                      </div>
                      
                      <div className="space-y-1 pt-2">
                        <p className="text-sm font-semibold" style={{ color: textCol }}>
                          Typography & Theme Performance
                        </p>
                        <p className="text-xs" style={{ color: textCol, opacity: 0.8 }}>
                          This text allows you to preview how your font family and text color interact with your chosen background. 
                          The buttons and header automatically adjust their contrast for accessibility.
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90">
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {editingTheme ? "Update Theme" : "Create Theme"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
